/**
 * Token Manager - Quản lý authentication tokens
 * Hỗ trợ cả client và server environments
 */

import { UPZI_SESSION_EXPIRED_EVENT_NAME } from '@/common';

/**
 * Token cache để tránh gọi API liên tục
 */
interface TokenCache {
  token: string | undefined;
  expiresAt: number;
}

let clientTokenCache: TokenCache | null = null;
let clientTokenRequest: Promise<string | undefined> | null = null;
let clientRefreshRequest: Promise<boolean> | null = null;
let clientTokenCacheVersion = 0;
const TOKEN_CACHE_TTL = 60 * 1000; // Cache 1 phút
const TOKEN_REFRESH_BUFFER_MS = 30 * 1000;
const CLIENT_REFRESH_LOCK_NAME = 'upzi-session-refresh';

function getJwtExpirationMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];

    if (!payload) return null;

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(padded)) as { exp?: unknown };

    return typeof decoded.exp === 'number' && Number.isFinite(decoded.exp)
      ? decoded.exp * 1000
      : null;
  } catch {
    return null;
  }
}

function getTokenCacheExpiration(token: string, now: number): number {
  const tokenExpiration = getJwtExpirationMs(token);

  return tokenExpiration === null
    ? now + TOKEN_CACHE_TTL
    : Math.min(
        now + TOKEN_CACHE_TTL,
        tokenExpiration - TOKEN_REFRESH_BUFFER_MS
      );
}

function shouldRefreshToken(token: string | undefined, now: number): boolean {
  if (!token) return true;

  const tokenExpiration = getJwtExpirationMs(token);

  return (
    tokenExpiration !== null && tokenExpiration - TOKEN_REFRESH_BUFFER_MS <= now
  );
}

async function readClientAccessToken(): Promise<string | undefined> {
  const response = await fetch('/api/auth/token', {
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) return undefined;

  const data = (await response.json()) as { token?: string | null };

  return data.token ?? undefined;
}

async function requestClientTokenRefresh(): Promise<boolean> {
  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    cache: 'no-store',
    credentials: 'include',
  });

  if (response.status === 401) return false;

  if (!response.ok) {
    throw new Error(`Token refresh temporarily failed (${response.status})`);
  }

  const data = (await response.json()) as { success?: boolean };

  return data.success === true;
}

async function refreshClientSession(): Promise<boolean> {
  const observedToken = await readClientAccessToken();

  if (!('locks' in navigator)) {
    return requestClientTokenRefresh();
  }

  return navigator.locks.request(CLIENT_REFRESH_LOCK_NAME, async () => {
    const latestToken = await readClientAccessToken();

    // Một tab khác đã refresh trong lúc tab này chờ lock.
    if (latestToken && latestToken !== observedToken) {
      clearTokenCache();

      return true;
    }

    return requestClientTokenRefresh();
  });
}

/**
 * Clear token cache (gọi khi login, refresh token hoặc logout)
 */
export function clearTokenCache(): void {
  clientTokenCache = null;
  clientTokenRequest = null;
  clientTokenCacheVersion += 1;
}

/**
 * Refresh session token
 * Ưu tiên gọi API route ở client để đảm bảo cookie được cập nhật
 * Trên server, sử dụng server action để tương tác trực tiếp với cookie store
 */
export async function refreshSessionToken(): Promise<boolean> {
  if (typeof window !== 'undefined') {
    // Các HttpClient được tạo theo từng microservice. Dùng một promise chung để
    // tránh nhiều request 401 cùng refresh một refresh token đã bị rotate.
    if (clientRefreshRequest) return clientRefreshRequest;

    // Clear cache trước khi refresh để lần retry luôn đọc access token mới.
    clearTokenCache();

    const refreshRequest = refreshClientSession();

    clientRefreshRequest = refreshRequest;

    try {
      return await refreshRequest;
    } finally {
      if (clientRefreshRequest === refreshRequest) {
        clientRefreshRequest = null;
      }
    }
  }

  // Server-side requests không được dùng promise chung vì cookie store là
  // request-scoped. Clear cache vẫn giữ hành vi đồng nhất với client.
  clearTokenCache();

  // Server-side: sử dụng server action
  try {
    const { refreshTokenAction } = await import('@/app/action');
    const result = await refreshTokenAction();

    if (result.success) return true;
    if (result.shouldLogout) return false;

    throw new Error(result.message ?? 'Token refresh temporarily failed');
  } catch (error) {
    console.error('Lỗi refresh token ở server:', error);

    throw error;
  }
}

/**
 * Lấy access token từ cookies với cache
 * Cache token để tránh gọi API liên tục
 */
export async function getAccessToken(): Promise<string | undefined> {
  // Client-side không thể truy cập httpOnly cookies trực tiếp
  // Nên phải gọi API route một lần, sau đó cache cả trạng thái guest.
  // Cache được xóa ngay khi login, refresh token hoặc logout.
  if (typeof window !== 'undefined') {
    if (clientTokenCache && clientTokenCache.expiresAt > Date.now()) {
      return clientTokenCache.token;
    }

    if (clientTokenRequest) return clientTokenRequest;

    let cacheVersion = clientTokenCacheVersion;
    const tokenRequest = (async () => {
      try {
        let token = await readClientAccessToken();
        const now = Date.now();

        if (shouldRefreshToken(token, now)) {
          try {
            await refreshSessionToken();
            token = await readClientAccessToken();
            cacheVersion = clientTokenCacheVersion;
          } catch (error) {
            // Token hiện tại vẫn có thể dùng được trong khoảng đệm refresh.
            if (!token) throw error;
          }
        }

        if (cacheVersion === clientTokenCacheVersion) {
          clientTokenCache = {
            token,
            expiresAt: token
              ? getTokenCacheExpiration(token, Date.now())
              : Number.POSITIVE_INFINITY,
          };
        }

        return token;
      } catch {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Token Manager] Không thể lấy token từ API route');
        }

        return undefined;
      }
    })();

    clientTokenRequest = tokenRequest;

    try {
      return await tokenRequest;
    } finally {
      if (clientTokenRequest === tokenRequest) {
        clientTokenRequest = null;
      }
    }
  }

  let token: string | undefined;

  try {
    const { getAccessTokenAction } = await import('@/app/action');

    token = await getAccessTokenAction();
  } catch {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Token Manager] Không thể lấy token từ server action');
    }
  }

  return token;
}

/**
 * Logout handler
 */
export async function handleLogout(): Promise<void> {
  if (typeof window !== 'undefined') {
    try {
      const { trackLogoutEvent } =
        await import('@/services/profile-tracking/logout-tracking');

      await trackLogoutEvent('expired_token');
    } catch (error) {
      console.error('Failed to track expired token logout:', error);
    }
  }

  // Clear token cache khi logout
  clearTokenCache();

  if (typeof window !== 'undefined') {
    // Client-side: gọi API route
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        cache: 'no-store',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Lỗi logout ở client:', error);
    } finally {
      // Let the app offer a fresh login before leaving the current page.
      window.dispatchEvent(new Event(UPZI_SESSION_EXPIRED_EVENT_NAME));
    }
  } else {
    // Server-side: sử dụng server action
    try {
      const { logoutAction } = await import('@/app/action');

      await logoutAction();
    } catch (error) {
      console.error('Lỗi logout ở server:', error);
    }
  }
}
